import type { PaginationProps, TableProps } from 'antd';
import { Pagination } from 'antd';
import React from 'react';
import { useDispatch } from 'react-redux';

import { SORT } from 'constants/FilterConstants';
import { useSelector } from 'hooks/useSelector';
import { impersonateUserId } from 'modules/auth/actions';
import {
  getImpersonateModalParameters,
  getImpersonatePager,
  getImpersonateStateUserId, getUserByIds,
  isImpersonateLoading
} from 'modules/users/selectors';
import { initColumnsWithSorter, resolveSort, resolveSortAntd } from 'services/SortingUtil';
import Table from 'ui/base/Table';

import * as actions from '../../../../modules/users/actions/impersonate';
import { useDisplayNameFormatter } from '../../../DisplayNameFormatter/useDisplayNameFormatter';
import useTableScrollY from '../../ModalDealerSelect/useTableScrollY';
import getColumns from '../columns';

/** All the table needs of a user: the rest of the record is read by the column renderers. */
type ImpersonateRow = { id: string };

const generateRowKey = ({ id: userId }: ImpersonateRow) => `user-${userId}`;

const UserTable = () => {
  const ref = React.useRef(null);

  const dispatch = useDispatch();
  const scrollY = useTableScrollY({ ref });
  const formatDisplayName = useDisplayNameFormatter();

  const pager = useSelector(getImpersonatePager);
  const loading = useSelector(isImpersonateLoading);
  const userIds = useSelector(getImpersonateStateUserId);
  const parameters = useSelector(getImpersonateModalParameters);
  // @ts-expect-error need to migrate selectors
  const dataSource = useSelector(state => getUserByIds(state, userIds));
  const columns = React.useMemo(() => {
    const direction = resolveSortAntd(parameters.sort.split(',').pop());
    return initColumnsWithSorter(getColumns(formatDisplayName), { displayName: direction });
  }, [parameters.sort, formatDisplayName]);

  const handlePaginationOnChange = React.useCallback<NonNullable<PaginationProps['onChange']>>(
    (page, pageSize) => {
      dispatch(actions.getImpersonateUsers({ ...parameters, page: page - 1, size: pageSize }));
    },
    [dispatch, parameters]
  );

  const handleOnChange = React.useCallback<NonNullable<TableProps<ImpersonateRow>['onChange']>>(
    (_, __, sorter) => {
      const { field: sorterField, order } = Array.isArray(sorter) ? sorter[0] : sorter;
      const field = Array.isArray(sorterField) ? sorterField.join('.') : sorterField;
      const sortDirection = resolveSort(order);
      const sort = `${field},${sortDirection}`;
      dispatch(actions.setImpersonateModalFilter(SORT, sort));
    },
    [dispatch]
  );

  const handleOnRow = React.useCallback<NonNullable<TableProps['onRow']>>(
    (record) => ({
      onClick: (event: React.MouseEvent) => {
        event.preventDefault();
        const { id: userId } = record;
        dispatch(impersonateUserId(userId));
        dispatch(actions.closeImpersonateModal());
      },
    }),
    [dispatch]
  );

  return (
    <div className="p-4 h-100 d-flex flex-column gap-3">
      <div className="h-100 overflow-hidden" ref={ref}>
        <Table
          columns={columns}
          loading={loading}
          pagination={false}
          onRow={handleOnRow}
          rowKey={generateRowKey}
          scroll={{ y: scrollY }}
          dataSource={dataSource}
          onChange={handleOnChange}
        />
      </div>
      {pager.totalElements > 0 && (
        <Pagination
          size="small"
          showSizeChanger
          pageSize={pager.size}
          current={pager.number + 1}
          total={pager.totalElements}
          onChange={handlePaginationOnChange}
        />
      )}
    </div>
  );
};

export default UserTable;
